import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Drawer,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CheckIcon from '@material-ui/icons/Check';
import LanguageIcon from '@material-ui/icons/Language';
import { Close } from 'icons/fds/Close';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Loading } from 'components/Loading';

import { locales } from '../constants';

export interface ApplicationFormLanguageSelectorProps {
  displayLocale: string;
  funnelLocale: string;
  availableLocales: Record<string, unknown>;
  onLocaleChange: (locale: string) => void;
  loading?: boolean;
}

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'right',
      gap: theme.spacing(1),
    },
    buttonText: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'left',
      gap: theme.spacing(0.5),
      width: '100%',
    },
    languageIcon: {
      display: 'none',
      marginRight: '16px',
      [theme.breakpoints.up('sm')]: {
        display: 'inline',
      },
    },
    arrowDropDownIcon: {
      marginRight: '40px',
      [theme.breakpoints.down('sm')]: {
        marginRight: '16px',
        marginBottom: '4px',
      },
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '64px',
    },
    mobileButtonText: {
      paddingTop: '8px',
      paddingBottom: '8px',
      height: '48px',
      display: 'flex',
    },
    mobileText: {
      marginTop: '-10%',
      textAlign: 'center',
      height: '64px',
      paddingTop: '15px',
      paddingBottom: '15px',
      fontSize: '1.3rem',
    },
    checkIcon: {
      color: '#3f7fe9 !important',
      right: '10px',
      position: 'fixed',
    },
    mobileIconButton: {
      alignSelf: 'flex-start',
      top: '19px',
      color: 'black',
    },
  }),
);

export const ApplicationFormLanguageSelector: React.FC<
  ApplicationFormLanguageSelectorProps
> = ({
  displayLocale,
  funnelLocale,
  availableLocales,
  onLocaleChange,
  loading = false,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [openDrawer, setOpenDrawer] = useState(false);
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));

  const [showSelector, setShowSelector] = useState<boolean>(false);

  useEffect(() => {
    if (Object.keys(availableLocales).length > 0) {
      setShowSelector(true);
    }
  }, [funnelLocale, availableLocales]);

  const languagesAsFormattedMessage: Record<string, React.ReactNode> = {};

  locales.forEach(locale => {
    languagesAsFormattedMessage[locale.code] = (
      <>
        <span>{locale.flag}</span>
        <span>{locale.language}</span>
      </>
    );
  });

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(1);

  const options = useMemo(() => {
    const allAvailableLocales = [...Object.keys(availableLocales)];
    const funnelLocaleAsObject = locales.find(locale =>
      locale.code.toLowerCase().includes(funnelLocale.toLowerCase()),
    );
    if (
      funnelLocaleAsObject &&
      !allAvailableLocales.includes(funnelLocaleAsObject.code)
    ) {
      allAvailableLocales.push(funnelLocaleAsObject.code);
    }
    return allAvailableLocales;
  }, [availableLocales, funnelLocale]);

  useEffect(() => {
    setSelectedIndex(
      options.findIndex(option => option.includes(displayLocale)), // we use .includes() to match shortcodes (ie. displayLocale === 'fr')
    );
  }, [displayLocale, options]);

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    setSelectedIndex(index);
    onLocaleChange(options[index]);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
    setOpenDrawer(true);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  if (!showSelector) {
    return null;
  }

  const button = (
    <React.Fragment>
      <div className={classes.root}>
        <Box display="inherit" visibility={loading ? 'visible' : 'hidden'}>
          <Loading size={16} />
        </Box>
        <ButtonGroup
          variant="text"
          disableRipple
          ref={anchorRef}
          aria-label="split button"
        >
          <Button
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open}
            aria-label={intl.formatMessage({
              defaultMessage: 'Select language',
              description: 'The aria label for the language selector',
            })}
            aria-haspopup="menu"
            onClick={handleToggle}
            color="inherit"
          >
            <div className={classes.buttonText}>
              <LanguageIcon className={classes.languageIcon} fontSize="small" />
              {languagesAsFormattedMessage[options[selectedIndex]]}
              <ArrowDropDownIcon className={classes.arrowDropDownIcon} />
            </div>
          </Button>
        </ButtonGroup>
        {!isMobile && (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{
              width: '15%',
            }}
            popperOptions={{
              modifiers: {
                offset: {
                  enabled: true,
                  offset: '-50px',
                },
              },
            }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu" autoFocusItem>
                      {options.map((option, index) => (
                        <MenuItem
                          key={option}
                          selected={index === selectedIndex}
                          onClick={event => handleMenuItemClick(event, index)}
                        >
                          <div className={classes.buttonText}>
                            {languagesAsFormattedMessage[option]}
                            {index === selectedIndex && (
                              <CheckIcon className={classes.checkIcon} />
                            )}
                          </div>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        )}
        {isMobile && (
          <Drawer
            role="dialog"
            open={openDrawer}
            onClose={() => setOpenDrawer(false)}
            anchor="bottom"
          >
            <div className="drawerHeader">
              <IconButton
                className={classes.mobileIconButton}
                onClick={() => setOpenDrawer(false)}
              >
                <Close />
              </IconButton>
              <Typography className={classes.mobileText} variant="h6">
                Select language
              </Typography>
            </div>

            <MenuList id="split-button-menu" autoFocusItem>
              {options.map((option, index) => (
                <MenuItem
                  key={option}
                  selected={index === selectedIndex}
                  style={{ paddingTop: '8px', height: '48px' }}
                  onClick={event => handleMenuItemClick(event, index)}
                >
                  <div className={classes.mobileButtonText}>
                    {languagesAsFormattedMessage[option]}
                    {index === selectedIndex && (
                      <CheckIcon className={classes.checkIcon} />
                    )}
                  </div>
                </MenuItem>
              ))}
            </MenuList>
          </Drawer>
        )}
      </div>
    </React.Fragment>
  );

  return button;
};
